import React, { FunctionComponent, useEffect, useRef, useState, useCallback } from 'react';
import { useTheme } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { useRobloxAuthentication } from '@rbx/auth';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import TryItOutWarningDialog from './TryItOutWarningDialog';
import TryItOutHighRiskWarningDialog from './TryItOutHighRiskWarningDialog';
import RiskLevel from '../types/RiskLevel';
import TryItOutAuthMode from '../types/TryItOutAuthMode';
import {
  getTryItOutButtonText,
  createTryItOutButtonClickHandler,
  getTryItOutHeader,
  getTryItOutButton,
  getOperationElement,
  getSwaggerUITryItOutButton,
  getOperationSummary,
  createHeaderDiv,
  createHeaderTextElement,
  createTryItOutButton,
  processBodyParamCancelButton,
  addCurlCopyButtonTooltip,
  processSwaggerUIButtons,
  isSwaggerUITryItOutButton,
  isSwaggerUITryItOutCancelButton,
  processExecuteButton,
  DisabledTryItOutButtonTooltipPortal,
  RiskLevelAlertPortal,
  AuthModeSelectorPortal,
  CurlCopyButtonTooltipPortal,
  logTryItOutEvent,
  type CurlCopyButtonTooltipData,
  type HighRiskWarningDialog,
} from './tryItOut';
import { DATA_ATTRIBUTES } from './tryItOut/constants';

interface TryItOutContainerProps {
  children: React.ReactNode;
  hasSupportedAuthTypes?: boolean;
  riskLevel?: RiskLevel;
  method?: string;
  url?: string;
  highRiskWarningDialog: HighRiskWarningDialog;
}

/**
 * Container component that manages the "Try It Out" functionality for Swagger UI operations.
 *
 * For manual testing in development, Legacy APIs > Users has good examples of different risk levels:
 * - LOW risk: GET /v1/users/{userId}
 * - MEDIUM risk: POST /v1/description
 * - HIGH risk: GET /v1/birthdate
 * - CRITICAL risk: POST /v1/birthdate
 */
const TryItOutContainer: FunctionComponent<TryItOutContainerProps> = ({
  children,
  hasSupportedAuthTypes = false,
  riskLevel = RiskLevel.CRITICAL,
  method = '',
  url = '',
  highRiskWarningDialog,
}) => {
  const { status, login } = useRobloxAuthentication();
  const theme = useTheme();
  const { translate } = useTranslation();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showHighRiskWarningDialog, setShowHighRiskWarningDialog] = useState(false);
  const [authModeSelectorContainer, setAuthModeSelectorContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [authMode, setAuthMode] = useState<TryItOutAuthMode>(
    status === 'unauthenticated' ? TryItOutAuthMode.NONE : TryItOutAuthMode.COOKIE,
  );
  const [disabledTryItOutButtonTooltipContainer, setDisabledTryItOutButtonTooltipContainer] =
    useState<HTMLElement | null>(null);
  const [riskLevelAlertContainer, setRiskLevelAlertContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [curlCopyButtonTooltipData, setCurlCopyButtonTooltipData] =
    useState<CurlCopyButtonTooltipData | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastClickedButtonRef = useRef<HTMLElement | null>(null);
  const isWarningDialogAcceptedRef = useRef(false);

  const isTryItOutEnabled = useCallback((): boolean => {
    return hasSupportedAuthTypes && riskLevel !== RiskLevel.CRITICAL;
  }, [hasSupportedAuthTypes, riskLevel]);

  const createTryItOutHeader = useCallback(
    (operationElement: Element | null) => {
      if (!operationElement) return;

      const swaggerTryItOutButton = getSwaggerUITryItOutButton(operationElement);
      if (!swaggerTryItOutButton) return;

      const enabled = isTryItOutEnabled();
      let containerElement: HTMLElement;

      if (enabled) {
        containerElement = createTryItOutButton(swaggerTryItOutButton, theme);

        const handleClick = createTryItOutButtonClickHandler(
          riskLevel,
          isWarningDialogAcceptedRef,
          containerElement,
          swaggerTryItOutButton,
          (element) => {
            lastClickedButtonRef.current = element;
          },
          setShowWarningDialog,
          method,
          url,
        );
        containerElement.addEventListener('click', handleClick);
      } else {
        containerElement = document.createElement('div');
        setDisabledTryItOutButtonTooltipContainer(containerElement);
      }

      // Create the "Try it out" header elements
      const headerDiv = createHeaderDiv(theme);
      headerDiv.appendChild(createHeaderTextElement(theme, enabled, translate('Label.TryItOut')));
      headerDiv.appendChild(containerElement);

      const operationSummary = getOperationSummary(operationElement);
      if (operationSummary) {
        operationSummary.insertAdjacentElement('afterend', headerDiv);
      }
    },
    [
      isTryItOutEnabled,
      riskLevel,
      setDisabledTryItOutButtonTooltipContainer,
      setShowWarningDialog,
      theme,
      translate,
      method,
      url,
    ],
  );

  // Helper method to create risk level alert and auth mode selector
  const createRiskLevelAlertAndAuthModeSelector = useCallback(
    (operationElement: Element | null) => {
      if (!operationElement) return;

      const tryItOutHeader = getTryItOutHeader(operationElement);
      if (!tryItOutHeader) {
        return;
      }

      // Create risk level alert first (if not already exists)
      let alertContainer = riskLevelAlertContainer;
      if (!alertContainer) {
        alertContainer = document.createElement('div');
        tryItOutHeader.insertAdjacentElement('afterend', alertContainer);
        setRiskLevelAlertContainer(alertContainer);
      }

      // Create auth mode selector second (if not already exists)
      if (!authModeSelectorContainer) {
        const selectorContainer = document.createElement('div');
        selectorContainer.style.marginTop = '16px';

        alertContainer.insertAdjacentElement('afterend', selectorContainer);

        setAuthModeSelectorContainer(selectorContainer);
      }
    },
    [
      riskLevelAlertContainer,
      authModeSelectorContainer,
      setRiskLevelAlertContainer,
      setAuthModeSelectorContainer,
    ],
  );

  // Helper method to remove risk level alert and auth mode selector
  const removeRiskLevelAlertAndAuthModeSelector = useCallback(() => {
    if (riskLevelAlertContainer) {
      riskLevelAlertContainer.remove();
      setRiskLevelAlertContainer(null);
    }

    if (authModeSelectorContainer) {
      authModeSelectorContainer.remove();
      setAuthModeSelectorContainer(null);
    }
  }, [
    riskLevelAlertContainer,
    authModeSelectorContainer,
    setRiskLevelAlertContainer,
    setAuthModeSelectorContainer,
  ]);

  // Process try-it-out buttons (handles creation and state changes)
  const processTryItOutButton = useCallback(
    (element: Element) => {
      if (!isSwaggerUITryItOutButton(element)) return;

      const operationElement = getOperationElement(element);

      if (!getTryItOutHeader(operationElement)) {
        createTryItOutHeader(operationElement);
      }

      const tryItOutButton = getTryItOutButton(operationElement);

      if (
        !operationElement ||
        !tryItOutButton ||
        tryItOutButton.getAttribute(DATA_ATTRIBUTES.PROCESSED) === 'true'
      )
        return;

      tryItOutButton.setAttribute(DATA_ATTRIBUTES.PROCESSED, 'true');

      const isTryItOutActive = isSwaggerUITryItOutCancelButton(element);

      tryItOutButton.textContent = getTryItOutButtonText(
        isTryItOutActive,
        translate('Action.TryItOut'),
        translate('Action.TryItOutClose'),
      );

      if (isTryItOutActive) {
        createRiskLevelAlertAndAuthModeSelector(operationElement);
      } else {
        removeRiskLevelAlertAndAuthModeSelector();
      }

      // Use setTimeout to reset flag after all mutations settle (Safari-specific fix)
      setTimeout(() => {
        tryItOutButton.removeAttribute(DATA_ATTRIBUTES.PROCESSED);
      }, 0);
    },
    [
      removeRiskLevelAlertAndAuthModeSelector,
      createTryItOutHeader,
      createRiskLevelAlertAndAuthModeSelector,
      translate,
    ],
  );

  // Monitor DOM changes to process Swagger UI buttons and maintain custom UI elements
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData') {
          // Handle text changes (Edit → Cancel for body params, Try it out ↔ Cancel for main buttons)
          const { parentElement } = mutation.target;
          if (parentElement) {
            processTryItOutButton(parentElement);
            processBodyParamCancelButton(
              parentElement,
              theme,
              translate('Action.TryItOutCancelEdit'),
            );
          }
        }

        if (mutation.type === 'childList') {
          // Process newly added DOM elements
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Process all Swagger UI buttons within the parent (to catch newly added buttons)
              processSwaggerUIButtons(element.parentElement, (swaggerButton) => {
                processTryItOutButton(swaggerButton);
                processBodyParamCancelButton(
                  swaggerButton,
                  theme,
                  translate('Action.TryItOutCancelEdit'),
                );
                addCurlCopyButtonTooltip(
                  swaggerButton,
                  setCurlCopyButtonTooltipData,
                  method,
                  url,
                  riskLevel,
                );
                if (riskLevel === RiskLevel.HIGH) {
                  processExecuteButton(
                    swaggerButton,
                    highRiskWarningDialog,
                    (buttonElement) => {
                      lastClickedButtonRef.current = buttonElement;
                    },
                    setShowHighRiskWarningDialog,
                  );
                }
              });
            }
          });

          // Process removed DOM elements to clean up when try-it-out buttons disappear
          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Check if a try-it-out button was nested in the removed element
              const tryItOutButton = getSwaggerUITryItOutButton(element);

              if (tryItOutButton && mutation.target.nodeType === Node.ELEMENT_NODE) {
                // Since the button is removed, we need to find the operation element from the mutation target
                const target = mutation.target as Element;
                const operationElement = getOperationElement(target);

                if (operationElement) {
                  // Clean up our custom elements when the underlying button disappears
                  removeRiskLevelAlertAndAuthModeSelector();

                  const tryItOutHeader = getTryItOutHeader(operationElement);
                  if (tryItOutHeader) {
                    tryItOutHeader.remove();
                  }
                }
              }
            }
          });
        }
      });
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [
    processTryItOutButton,
    removeRiskLevelAlertAndAuthModeSelector,
    setCurlCopyButtonTooltipData,
    theme,
    translate,
    method,
    url,
    riskLevel,
    highRiskWarningDialog,
    setShowHighRiskWarningDialog,
    lastClickedButtonRef,
  ]);

  const handleWarningDialogClose = () => {
    logTryItOutEvent(DocsSiteEventType.TryItOutClickCancelWarning, {
      method,
      url,
      riskLevel,
    });

    setShowWarningDialog(false);
  };

  const handleWarningDialogContinue = () => {
    isWarningDialogAcceptedRef.current = true;

    logTryItOutEvent(DocsSiteEventType.TryItOutClickAcceptWarning, {
      method,
      url,
      riskLevel,
    });

    setShowWarningDialog(false);

    // Re-click the button that was clicked before the warning dialog was shown
    if (lastClickedButtonRef.current) {
      lastClickedButtonRef.current.click();
    }
  };

  const handleHighRiskWarningDialogClose = () => {
    logTryItOutEvent(DocsSiteEventType.TryItOutClickCancelHighRiskWarning, {
      method,
      url,
      riskLevel,
      authMode,
    });

    setShowHighRiskWarningDialog(false);
  };

  const handleHighRiskWarningDialogContinue = () => {
    highRiskWarningDialog.setAccepted(true);

    logTryItOutEvent(DocsSiteEventType.TryItOutClickAcceptHighRiskWarning, {
      method,
      url,
      riskLevel,
      authMode,
    });

    setShowHighRiskWarningDialog(false);

    // Re-click the button that was clicked before the warning dialog was shown
    if (lastClickedButtonRef.current) {
      lastClickedButtonRef.current.click();
    }
  };

  const renderDisabledTryItOutButtonTooltipPortal = useCallback(() => {
    if (isTryItOutEnabled()) return null;

    if (!hasSupportedAuthTypes) {
      return (
        <DisabledTryItOutButtonTooltipPortal
          container={disabledTryItOutButtonTooltipContainer}
          title={translate('Description.TryItOutAuthNotSupported')}
          fragmentKey='unsupported-auth-mode-tooltip'
        />
      );
    }
    if (riskLevel === RiskLevel.CRITICAL) {
      return (
        <DisabledTryItOutButtonTooltipPortal
          container={disabledTryItOutButtonTooltipContainer}
          title={translate('Description.TryItOutCriticalRiskDisabled')}
          fragmentKey='critical-risk-tooltip'
        />
      );
    }

    return null;
  }, [
    isTryItOutEnabled,
    disabledTryItOutButtonTooltipContainer,
    hasSupportedAuthTypes,
    riskLevel,
    translate,
  ]);

  return (
    <>
      <div ref={containerRef}>{children}</div>

      {renderDisabledTryItOutButtonTooltipPortal()}
      <RiskLevelAlertPortal container={riskLevelAlertContainer} riskLevel={riskLevel} />
      <AuthModeSelectorPortal
        container={authModeSelectorContainer}
        authMode={authMode}
        setAuthMode={setAuthMode}
        theme={theme}
        {...(status === 'unauthenticated' && { login })}
      />
      <CurlCopyButtonTooltipPortal data={curlCopyButtonTooltipData} />

      <TryItOutWarningDialog
        open={showWarningDialog}
        onClose={handleWarningDialogClose}
        onContinue={handleWarningDialogContinue}
      />

      <TryItOutHighRiskWarningDialog
        open={showHighRiskWarningDialog}
        onClose={handleHighRiskWarningDialogClose}
        onContinue={handleHighRiskWarningDialogContinue}
      />
    </>
  );
};

export default TryItOutContainer;
